import React, { useState } from 'react';
import styles from './UpdatesTabContent.module.css';
import { createSwagUpdate, fetchSwagUpdateDetails } from '../services/unitService';

export default function AddSwagUpdate({ onCancel, onSuccess }) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [logo, setLogo] = useState(null); // {file, src}
    const [images, setImages] = useState([]); // [{file, src}]
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const validate = () => {
        const errs = {};
        if (!title.trim()) errs.title = 'Title is required.';
        if (!description.trim()) errs.description = 'Description is required.';
        if (!logo) errs.logo = 'Logo is required.';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleLogoUpload = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    setLogo({ file, src: event.target.result });
                };
                reader.readAsDataURL(file);
            }
        };
        fileInput.click();
    };

    const handleImagesUpload = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.multiple = true;
        fileInput.onchange = (e) => {
            const files = Array.from(e.target.files);
            const readers = files.map(file => {
                return new Promise(resolve => {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        resolve({ file, src: event.target.result });
                    };
                    reader.readAsDataURL(file);
                });
            });
            Promise.all(readers).then(newImgs => {
                setImages(prev => [...prev, ...newImgs]);
            });
        };
        fileInput.click();
    };

    const removeImage = (idx) => {
        setImages(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');
        if (!validate()) return;
        setLoading(true);
        try {
            // Prepare FormData
            const formData = new FormData();
            formData.append('title', title);
            formData.append('description', description);
            if (logo) {
                formData.append('logo', logo.file);
            }
            images.forEach((imgObj) => {
                formData.append('images', imgObj.file);
            });
            // print the formData keys for debugging
            for (let key of formData.keys()) {
                console.log(key, formData.getAll(key));
                console.log('-----------');
            }
            const res = await createSwagUpdate(formData);
            if (res.data && res.data._id) {
                const details = await fetchSwagUpdateDetails(res.data._id);
                onSuccess(details.data);
            } else {
                onSuccess(null);
            }
        } catch (err) {
            setSubmitError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.updatesContainer}>
            <div className={styles.headerContainer}>
                <div className={styles.headerLeft}>
                    <button className={styles.cancelButton} onClick={onCancel} disabled={loading}>
                        Cancel
                    </button>
                    <h2 className={styles.headerTitle}>Add Swag Update</h2>
                </div>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.saveButton} onClick={handleSubmit} disabled={loading}>
                        {loading ? 'Adding...' : 'Add Update'}
                    </button>
                </div>
            </div>
            <div className={styles.unitUpdateFormContainer}>
                <form className={styles.unitUpdateForm} onSubmit={handleSubmit}>
                    <div className={styles.formSection}>
                        <div className={styles.formGroup}>
                            <label className={styles.unitUpdateLabel} htmlFor="title">Title</label>
                            <input
                                id="title"
                                className={styles.editTextarea}
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                type="text"
                                disabled={loading}
                                placeholder="Enter title..."
                                style={{ width: '100%' }}
                            />
                            {errors.title && <div className={styles.formError}>{errors.title}</div>}
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.unitUpdateLabel} htmlFor="description">Description</label>
                            <textarea
                                id="description"
                                className={styles.editTextarea}
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                rows={6}
                                disabled={loading}
                                placeholder="Enter description..."
                                style={{ width: '100%' }}
                            />
                            {errors.description && <div className={styles.formError}>{errors.description}</div>}
                        </div>
                    </div>
                    <div className={styles.formSection}>
                        <div className={styles.formGroup}>
                            <label className={styles.unitUpdateLabel}>Logo</label>
                            {!logo ? (
                                <div className={styles.uploadContainer}>
                                    <div className={styles.uploadIcon}>
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7,10 12,15 17,10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                    </div>
                                    <p className={styles.uploadText}>Upload Logo</p>
                                    <button type="button" className={styles.uploadButton} onClick={handleLogoUpload} disabled={loading}>
                                        Choose File
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.imageItem}>
                                    <img src={logo.src} alt="Logo Preview" />
                                    <button type="button" className={styles.removeButton} onClick={() => setLogo(null)} disabled={loading}>
                                        Remove
                                    </button>
                                </div>
                            )}
                            {errors.logo && <div className={styles.formError}>{errors.logo}</div>}
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.unitUpdateLabel}>Images</label>
                            {images.length === 0 ? (
                                <div className={styles.uploadContainer}>
                                    <div className={styles.uploadIcon}>
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7,10 12,15 17,10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                        </svg>
                                    </div>
                                    <p className={styles.uploadText}>Upload Images</p>
                                    <button type="button" className={styles.uploadButton} onClick={handleImagesUpload} disabled={loading}>
                                        Choose Files
                                    </button>
                                </div>
                            ) : (
                                <div className={styles.imageGrid}>
                                    {images.map((img, idx) => (
                                        <div className={styles.imageItem} key={idx}>
                                            <img src={img.src} alt={`Preview ${idx + 1}`} />
                                            <button type="button" className={styles.removeButton} onClick={() => removeImage(idx)} disabled={loading}>
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <div className={styles.imageItem}>
                                        <button type="button" className={styles.uploadButton} onClick={handleImagesUpload} disabled={loading}>
                                            + Add More
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        {submitError && <div className={styles.formError}>{submitError}</div>}
                    </div>
                </form>
            </div>
        </div>
    );
}